// 일정 관리(v3.26) — 월간 캘린더(42셀 자체 렌더, 외부 라이브러리 미도입).
import { cn } from "@/lib/cn";
import type { ScheduleItem } from "@/api/types";
import { dayKey, fieldDotClass, isSameDay, monthGridDays } from "./utils";

interface Props {
  monthDate: Date;              // 표시 중인 월(1일)
  schedules: ScheduleItem[];    // 42셀 범위의 일정
  selectedKey: string | null;   // 선택된 날짜(YYYY-MM-DD)
  onSelectDate: (key: string) => void;
}

export default function MonthCalendar({ monthDate, schedules, selectedKey, onSelectDate }: Props) {
  const days = monthGridDays(monthDate);
  const today = new Date();
  const byDay = new Map<string, ScheduleItem[]>();
  for (const s of schedules) {
    const k = dayKey(new Date(s.start_at));
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k)!.push(s);
  }

  return (
    <table className="w-full table-fixed border-collapse select-none">
      <thead>
        <tr>
          {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
            <th
              key={d}
              className={cn(
                "border-b border-border pb-1 text-xs font-semibold text-muted-foreground",
                i === 0 && "text-red-500",
                i === 6 && "text-sky-600",
              )}
            >
              {d}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 6 }, (_, w) => (
          <tr key={w}>
            {days.slice(w * 7, w * 7 + 7).map((d) => {
              const k = dayKey(d);
              const inMonth = d.getMonth() === monthDate.getMonth();
              const items = byDay.get(k) ?? [];
              return (
                <td
                  key={k}
                  onClick={() => onSelectDate(k)}
                  className={cn(
                    "h-12 cursor-pointer border border-border/60 p-1 align-top sm:h-14",
                    selectedKey === k && "bg-primary/10",
                    isSameDay(d, today) && "ring-2 ring-inset ring-primary",
                  )}
                >
                  <span
                    className={cn(
                      "text-xs",
                      inMonth ? "text-foreground" : "text-muted-foreground/40",
                      inMonth && d.getDay() === 0 && "text-red-500",
                      inMonth && d.getDay() === 6 && "text-sky-600",
                    )}
                  >
                    {d.getDate()}
                  </span>
                  {items.length > 0 && (
                    <span className="mt-0.5 flex flex-wrap items-center gap-0.5">
                      {items.slice(0, 3).map((s) => (
                        <span
                          key={s.id}
                          className={cn("h-1.5 w-1.5 rounded-full", fieldDotClass(s.field))}
                          title={`[${s.category}/${s.field}] ${s.title}`}
                        />
                      ))}
                      {items.length > 3 && (
                        <span className="text-[9px] leading-none text-muted-foreground">
                          +{items.length - 3}
                        </span>
                      )}
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
